/* ============================================================
   PDP TEMPLATE — INTERACTIVITY
   - Gallery (next/prev, pager, thumb sync, swipe)
   - Pack selector (native radios, price sync, variant-id binding)
   - FAQ accordion (aria-expanded / aria-controls)
   - Sticky ATC visibility
   - Announcement-bar countdown + dispatch cutoff

   PDP v2.1 VERBATIM PORT — Phase C2.5 (+ C2.8 reviewer fixes,
   + Mobile-QA fix batch EDIT 4, + section-slice EDIT 5).
   This file is v2.1's script.js ported. Five named EDITs to v2.1's
   source plus two additions; everything else is byte-verbatim:

     EDIT 1 — initGallery pager build. v2.1 assigned the pager dot
       markup as a string with the .inner-HTML property. The Store
       Builder theme runs a security hook that blocks that property; the
       pager dots are now built with document.createElement / appendChild.

     EDIT 2 — initDispatchEstimate writer. v2.1 assigned the estimate
       line as a string with the .inner-HTML property. Same hook; the
       estimate line is now built with createElement / appendChild.

     EDIT 3 — pack selector. v2.1's initPackSelector drove a
       `[role=radiogroup]` of `[role=radio]` divs with manual
       aria-checked + arrow-key handling. The Shopify commerce port
       (Phase C) replaced the mockup packs with native
       `<input type="radio" name="sb_tier">` inside `<label class=pack>`,
       so keyboard + checked state are now browser-native. initPackSelector
       is rewritten as a native-radio `change` handler that: writes the
       chosen Shopify variant ID into the hidden `[name=id]` the
       `<product-form>` submits; mirrors the tier's data-* values into
       every `[data-sync]` price node (buy-box, CTA, value-stack,
       sticky ATC, inline-offer + final-CTA echoes); toggles
       `.pack.is-selected`; and handles the `.no-compare` state for
       variants with no compare-at price. It runs once on load. All its
       DOM lookups are rooted at the `<product-form>` element, not the
       document (C2.8 FIX 5).

     EDIT 4 — initStickyATC observe target (Mobile-QA fix batch,
       Issue 2). v2.1's initStickyATC observed the hero "Add to cart"
       button `[data-cta-hero]` with a `-20% 0px 0px 0px` rootMargin.
       The Store Builder port added hero benefit bullets ABOVE that
       button inside the `.buybox`, so the sticky bar surfaced while
       the bullets were still on screen and overlapped them. The
       observer now watches the whole `.buybox` (the element that
       contains the bullets AND the button) with a `0px` rootMargin,
       so the sticky bar appears only after the entire buy decision
       has scrolled past. The function's null-guard is unchanged.

     EDIT 5 — theme-editor lifecycle (PDP section-slice). v2.1's PDP was
       ONE Shopify section, so the original boot used a one-shot
       module-level "already booted" window flag — its own comment said
       this worked "because the PDP is a single section". The
       section-slice splits the PDP into 15 separate Shopify sections,
       all still served by this ONE shared asset (loaded once by the
       `pdp` layout). In the Shopify theme editor, editing / adding /
       removing one section fires `shopify:section:load` and re-renders
       just that section's DOM with NO event handlers; a one-shot flag
       would block re-init and leave that section interactivity-dead
       until a full reload.
       EDIT 5 drops the one-shot re-entry flag, makes every `init*`
       function idempotent — each listener attach is guarded with a
       `data-sb-bound` attribute so a re-run never double-binds — and
       adds `shopify:section:load` / `shopify:section:unload` listeners
       (gated on `Shopify.designMode`, so they fire ONLY inside the
       theme editor). The `IntersectionObserver` and the countdown
       `setInterval` id are held in module scope and disconnected /
       cleared before any re-creation. The LIVE STOREFRONT behaviour is
       byte-for-byte unchanged: the script still loads once, the full
       DOM exists at `DOMContentLoaded`, `boot()` runs once, and every
       init still works exactly as before. EDIT 5 is purely additive
       for theme-editor correctness.

     ADDITION A — initScrollToTiers. The two inline-offer CTAs + the
       final-CTA button are not submit buttons; they smooth-scroll the
       buyer to the buy decision at #price_tiers.

     ADDITION B — superseded by EDIT 5. The original ADDITION B was a
       one-shot module-level double-init guard; the section-slice makes
       that guard wrong (it blocks the theme editor's legitimate
       re-init), so EDIT 5 replaces it with per-element idempotency.

   COMMERCE NOTE — variant binding depends on this file. If the script
   fails to load, the product form submits the hidden id input's
   server-rendered value. That value is the DEFAULT pack's variant (the
   Liquid header pre-fills it), so the JS-down fallback still adds the
   pack rendered as selected — coherently, not a mismatched variant.
   ============================================================ */

(function () {
  'use strict';

  /* ------------- IDEMPOTENCY HELPER (EDIT 5) ------------- */
  // Returns true the FIRST time it sees a given (element, key) pair and
  // false on every later call, marking the element with a `data-sb-bound`
  // token. Every listener attach is gated on this so a `boot()` re-run in
  // the Shopify theme editor never double-binds a handler. On the live
  // storefront `boot()` runs exactly once, so this always returns true and
  // behaviour is identical to the pre-EDIT-5 unguarded code.
  function bindOnce(el, key) {
    if (!el) return false;
    const existing = el.getAttribute('data-sb-bound');
    const tokens = existing ? existing.split(' ') : [];
    if (tokens.indexOf(key) !== -1) return false;
    tokens.push(key);
    el.setAttribute('data-sb-bound', tokens.join(' ').trim());
    return true;
  }

  /* ------------- GALLERY ------------- */
  function initGallery(root) {
    if (!root) return;
    const slides = Array.from(root.querySelectorAll('.slide'));
    const thumbs = Array.from(root.querySelectorAll('.thumb'));
    const pager  = root.querySelector('.gallery-pager');
    const prev   = root.querySelector('.gallery-prev');
    const next   = root.querySelector('.gallery-next');
    let idx = 0;

    // build pager dots
    // EDIT 1 — v2.1 assigned the dot markup as a string. The security
    // hook blocks that; build the dots with createElement instead.
    // EDIT 5 — replaceChildren() makes this rebuild idempotent on its
    // own: a re-run wipes the old dots and rebuilds, never appending.
    if (pager) {
      pager.replaceChildren();
      slides.forEach(function (_, i) {
        const dot = document.createElement('span');
        dot.dataset.i = String(i);
        if (i === 0) dot.className = 'is-active';
        pager.appendChild(dot);
      });
    }

    function go(i) {
      idx = (i + slides.length) % slides.length;
      slides.forEach((s, n) => s.classList.toggle('is-active', n === idx));
      thumbs.forEach((t, n) => t.classList.toggle('is-active', n === idx));
      if (pager) {
        Array.from(pager.children).forEach((d, n) => d.classList.toggle('is-active', n === idx));
      }
    }

    // EDIT 5 — guard every listener attach with bindOnce so a theme-editor
    // re-init of the hero section cannot stack a second click/swipe/key
    // handler on the same element.
    if (prev && bindOnce(prev, 'gallery-prev')) {
      prev.addEventListener('click', () => go(idx - 1));
    }
    if (next && bindOnce(next, 'gallery-next')) {
      next.addEventListener('click', () => go(idx + 1));
    }
    thumbs.forEach((t, i) => {
      if (bindOnce(t, 'gallery-thumb')) t.addEventListener('click', () => go(i));
    });
    if (pager && bindOnce(pager, 'gallery-pager')) {
      pager.addEventListener('click', e => {
        const t = e.target.closest('[data-i]');
        if (t) go(+t.dataset.i);
      });
    }

    // swipe (mobile) + keyboard — both rooted on the gallery element
    if (bindOnce(root, 'gallery-root')) {
      let startX = null;
      root.addEventListener('touchstart', e => {
        startX = e.touches[0].clientX;
      }, { passive: true });
      root.addEventListener('touchend', e => {
        if (startX == null) return;
        const dx = e.changedTouches[0].clientX - startX;
        if (Math.abs(dx) > 40) go(idx + (dx < 0 ? 1 : -1));
        startX = null;
      });
      root.addEventListener('keydown', e => {
        if (e.key === 'ArrowLeft')  { e.preventDefault(); go(idx - 1); }
        if (e.key === 'ArrowRight') { e.preventDefault(); go(idx + 1); }
      });
    }
  }

  /* ------------- PACK SELECTOR ------------- */
  // EDIT 3 — rewritten for the Phase C Shopify commerce wiring. v2.1's
  // initPackSelector drove `[role=radio]` divs; the port uses native
  // `<input type="radio" name="sb_tier">`. Keyboard + checked state are
  // browser-native now; this handler only does the price sync, the
  // hidden `[name=id]` variant binding and the `.is-selected` /
  // `.no-compare` visual state.
  function initPackSelector() {
    // FIX 5 — root all commerce lookups at the <product-form> element
    // (the buy-box commerce root), not the document. The pack radios and
    // the single hidden id input both live inside it.
    const productForm = document.querySelector('product-form');
    if (!productForm) return;
    const radios = Array.from(
      productForm.querySelectorAll('input.pack-input[name="sb_tier"]'),
    );
    if (!radios.length) return;

    // The single hidden input the <product-form> submits. product-form.js
    // does querySelector("[name=id]") and expects exactly one.
    const idInput = productForm.querySelector('input[name="id"]');

    function syncKey(key, value) {
      // Mirror v2.1's behaviour: write to EVERY [data-sync] node on the
      // page (buy-box, CTA, value-stack, sticky ATC, and the lower
      // inline-offer + final-CTA echoes), so a pack switch is reflected
      // everywhere at once. EDIT 5 keeps this a page-wide
      // `document.querySelectorAll` ON PURPOSE — the price fan-out has
      // to cross the now-separate sliced sections.
      document
        .querySelectorAll('[data-sync="' + key + '"]')
        .forEach(function (el) {
          el.textContent = value;
        });
    }

    function select(radio) {
      if (!radio) return;

      // Couple the variant ID + the displayed price so they never
      // diverge. A selectable tier always carries a real data-variant-id
      // (an un-wired/sold-out tier is rendered `disabled` server-side and
      // can never reach here), so the hidden id always tracks the pack.
      if (idInput && radio.dataset.variantId) {
        idInput.value = radio.dataset.variantId;
      }

      // The price-family data-* values are DERIVED money strings from the
      // variant's real price (C2.8 FIX 8) — internally consistent. An
      // empty data-compare means the variant has no compare_at_price.
      const price    = radio.dataset.price || '';
      const compare  = radio.dataset.compare || '';
      const saveAmt  = radio.dataset.saveAmt || '';
      const savePct  = radio.dataset.savePct || '';
      const label    = radio.dataset.packLabel || '';
      const bonus    = radio.dataset.bonusTotal || '';
      const bnpl3    = radio.dataset.bnpl3 || '';

      if (price)  syncKey('price', price);
      syncKey('compare', compare);
      syncKey('save-amt', saveAmt);
      syncKey('save-pct', savePct);
      if (label)  syncKey('pack-label', label);
      syncKey('bonus-total', bonus);
      // BNPL instalment tracks the selected pack (price / 3, derived per tier
      // in the Liquid). Only fans out when the operator-gated BNPL line exists.
      if (bnpl3)  syncKey('bnpl3', bnpl3);

      // No-compare handling (C2.4 + C2.8 FIX 3) — a variant with no
      // compare_at_price has a blank data-compare. Toggle `.no-compare`
      // on the buy-box, sticky ATC, both inline-offer cards AND the final
      // CTA so every struck-through price + savings figure is hidden.
      const hasCompare = compare.trim() !== '';
      document
        .querySelectorAll('.buybox, .sticky-atc, .inline-offer-card, .final')
        .forEach(function (scope) {
          scope.classList.toggle('no-compare', !hasCompare);
        });

      // Toggle the selected-pack visual state.
      radios.forEach(function (r) {
        const packLabel = r.closest('.pack');
        if (packLabel) packLabel.classList.toggle('is-selected', r === radio);
      });
    }

    // EDIT 5 — guard the per-radio change binding with bindOnce so a
    // theme-editor re-init of the hero section never stacks a second
    // change handler on the same radio.
    radios.forEach(function (radio) {
      if (!bindOnce(radio, 'pack-radio')) return;
      radio.addEventListener('change', function () {
        if (radio.checked) select(radio);
      });
    });

    // Run once on load so the buy-box / CTA / sticky ATC reflect the
    // server-rendered checked tier. Prefer the checked radio; fall back
    // to the first SELECTABLE (non-disabled) radio — never a disabled
    // tier, which would mis-sync the price to an un-buyable pack.
    const checked =
      radios.filter(function (r) { return r.checked; })[0] ||
      radios.filter(function (r) { return !r.disabled; })[0];
    if (checked) select(checked);
  }

  /* ------------- QUANTITY STEPPER ------------- */
  // Commerce design upgrade (P1) — the buy-box quantity stepper. The two
  // buttons (data-qty-down / data-qty-up) adjust a clamped integer (min 1)
  // and write it into the hidden name="quantity" input the <product-form>
  // submits, and mirror it into the [data-qty-val] display. Rooted at the
  // <product-form> commerce root, like initPackSelector; bindOnce-guarded so
  // a theme-editor re-init never stacks a second handler. JS-down, the hidden
  // quantity input keeps its server value of 1.
  function initQtyStepper() {
    const productForm = document.querySelector('product-form');
    if (!productForm) return;
    const qtyInput = productForm.querySelector('input[name="quantity"]');
    const valEl = productForm.querySelector('[data-qty-val]');
    const down = productForm.querySelector('[data-qty-down]');
    const up = productForm.querySelector('[data-qty-up]');
    if (!qtyInput || !valEl || !down || !up) return;

    function read() {
      const n = parseInt(qtyInput.value, 10);
      return Number.isFinite(n) && n > 0 ? n : 1;
    }
    function write(n) {
      const clamped = n < 1 ? 1 : n;
      qtyInput.value = String(clamped);
      valEl.textContent = String(clamped);
    }
    write(read());

    if (bindOnce(down, 'qty-down')) {
      down.addEventListener('click', function () { write(read() - 1); });
    }
    if (bindOnce(up, 'qty-up')) {
      up.addEventListener('click', function () { write(read() + 1); });
    }
  }

  /* ------------- SCROLL-TO-TIERS CTAs ------------- */
  // The 2 inline-offer "Add to cart" buttons + the final-CTA button are
  // NOT submit buttons (only the hero buy-box + the sticky ATC transact).
  // They route the buyer back to the one buy decision at #price_tiers.
  // EDIT 5 keeps the page-wide `[data-scroll-to-tiers]` query ON PURPOSE
  // — the offer + final-CTA buttons now live in separate sliced sections
  // and must all still resolve to the hero's #price_tiers.
  function initScrollToTiers() {
    const target = document.getElementById('price_tiers');
    if (!target) return;
    document.querySelectorAll('[data-scroll-to-tiers]').forEach(function (btn) {
      // EDIT 5 — bindOnce so a theme-editor re-init never stacks a
      // second scroll handler on a button.
      if (!bindOnce(btn, 'scroll-to-tiers')) return;
      btn.addEventListener('click', function () {
        const reduce = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        target.scrollIntoView({ behavior: reduce ? 'auto' : 'smooth', block: 'center' });
      });
    });
  }

  /* ------------- FAQ ACCORDION ------------- */
  // EDIT 5 — each `.faq-q` button binding is guarded with bindOnce so a
  // theme-editor re-init of the FAQ section never stacks a second toggle
  // handler. On the live storefront boot() runs once, so this is
  // behaviourally identical to v2.1's unguarded loop.
  function initFAQ() {
    document.querySelectorAll('.faq-item').forEach(item => {
      const btn = item.querySelector('.faq-q');
      const panel = item.querySelector('.faq-a');
      if (!btn || !panel) return;
      if (!bindOnce(btn, 'faq-q')) return;
      btn.addEventListener('click', () => {
        const open = item.getAttribute('aria-expanded') === 'true';
        item.setAttribute('aria-expanded', open ? 'false' : 'true');
        btn.setAttribute('aria-expanded', open ? 'false' : 'true');
      });
    });
  }

  /* ------------- STICKY ATC VISIBILITY ------------- */
  // EDIT 4 — sticky-ATC observe target (Mobile-QA fix, Issue 2). v2.1
  // observed `[data-cta-hero]` (the hero "Add to cart" BUTTON) with a
  // `-20% 0px 0px 0px` rootMargin. The Store Builder port added benefit
  // bullets ABOVE that button inside the buy-box, so the sticky bar
  // surfaced while the bullets were still on screen and the fixed bar
  // painted over them. The observer now watches the whole `.buybox`
  // (the element that CONTAINS the bullets + the button) with a `0px`
  // rootMargin, so the bar appears only once the entire buy decision
  // has scrolled away. The null-guard is unchanged.
  //
  // EDIT 5 — the IntersectionObserver is held in the module-scope
  // `stickyObserver` variable and `.disconnect()`-ed before a new one is
  // created, so a theme-editor re-init of the hero section cannot leave
  // a stale observer running alongside the fresh one. On the live
  // storefront initStickyATC runs once: stickyObserver is null, nothing
  // is disconnected, one observer is created — identical to before. The
  // observed `.buybox` element is ALSO recorded in `stickyObserverTarget`
  // so the scoped `shopify:section:unload` teardown can tell whether the
  // section being removed is the one that owns this observer.
  function initStickyATC() {
    const sticky = document.querySelector('.sticky-atc');
    const heroCta = document.querySelector('.buybox');
    if (!sticky || !heroCta) return;

    // Tear down a prior observer before re-creating (EDIT 5).
    if (stickyObserver) stickyObserver.disconnect();

    // Surface the sticky bar once the buy-box has fully scrolled past
    stickyObserver = new IntersectionObserver(entries => {
      entries.forEach(e => {
        sticky.classList.toggle('is-visible', !e.isIntersecting);
      });
    }, { rootMargin: '0px', threshold: 0 });

    stickyObserver.observe(heroCta);
    stickyObserverTarget = heroCta;
  }

  /* ------------- COUNTDOWN + DISPATCH ESTIMATE ------------- */
  // EDIT 5 — the countdown interval id is held in the module-scope
  // `countdownIntervalId` variable and `clearInterval()`-ed before a new
  // interval is created, so a theme-editor re-init of the announcement
  // section cannot leave two intervals ticking the same node. On the
  // live storefront initCountdown runs once: countdownIntervalId is null,
  // nothing is cleared, one interval is created — identical to before.
  // The `[data-countdown]` element is ALSO recorded in `countdownTarget`
  // so the scoped `shopify:section:unload` teardown can tell whether the
  // section being removed is the one that owns this interval.
  function initCountdown() {
    // Announcement countdown: a fixed-target end-of-day deadline (resets nightly)
    const el = document.querySelector('[data-countdown]');
    if (!el) return;
    countdownTarget = el;
    const end = new Date();
    end.setHours(23, 59, 59, 999);

    function tick() {
      const now = new Date();
      let s = Math.max(0, Math.floor((end - now) / 1000));
      const h = String(Math.floor(s / 3600)).padStart(2, '0'); s -= h * 3600;
      const m = String(Math.floor(s / 60)).padStart(2, '0'); s -= m * 60;
      const ss = String(s).padStart(2, '0');
      el.textContent = `${h}:${m}:${ss}`;
    }
    tick();

    // Clear a prior interval before re-creating (EDIT 5).
    if (countdownIntervalId !== null) {
      clearInterval(countdownIntervalId);
      countdownIntervalId = null;
    }
    if (!window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
      countdownIntervalId = setInterval(tick, 1000);
    }
  }

  function initDispatchEstimate() {
    const el = document.querySelector('[data-dispatch]');
    if (!el) return;
    // Order in next X hours, arrives on weekday + N business days
    const now = new Date();
    const cutoffHour = 16; // 4pm dispatch cutoff
    const today = new Date(now);
    const cutoff = new Date(today);
    cutoff.setHours(cutoffHour, 0, 0, 0);

    let hoursLeft = Math.max(0, Math.floor((cutoff - now) / (1000 * 60 * 60)));
    let dispatchToday = now < cutoff;

    // arrival = +2 business days from dispatch
    let arrive = new Date(dispatchToday ? today : new Date(today.getTime() + 86400000));
    let added = 0;
    while (added < 2) {
      arrive.setDate(arrive.getDate() + 1);
      const d = arrive.getDay();
      if (d !== 0 && d !== 6) added++;
    }
    const days = ['Sun','Mon','Tue','Wed','Thu','Fri','Sat'];
    const months = ['Jan','Feb','Mar','Apr','May','Jun','Jul','Aug','Sep','Oct','Nov','Dec'];
    const arriveStr = `${days[arrive.getDay()]} ${arrive.getDate()} ${months[arrive.getMonth()]}`;

    // EDIT 2 — v2.1 assigned the estimate line as a string. The security
    // hook blocks that; build the line with createElement so the <b>
    // emphasis on the hours + arrival date is preserved.
    // EDIT 5 — replaceChildren() makes this writer idempotent on its own:
    // a re-run wipes the old line and rebuilds, never appending.
    el.replaceChildren();
    function append(text) { el.appendChild(document.createTextNode(text)); }
    function appendBold(text) {
      const b = document.createElement('b');
      b.textContent = text;
      el.appendChild(b);
    }
    if (dispatchToday && hoursLeft > 0) {
      append('Order in the next ');
      appendBold(hoursLeft + 'h');
      append(' · arrives ');
      appendBold(arriveStr);
    } else {
      append('Ships next business day · arrives ');
      appendBold(arriveStr);
    }
  }

  /* ------------- LIFECYCLE STATE (EDIT 5) ------------- */
  // Module-scope handles for the one resource each that must be torn down
  // before a re-init. Null until first initialised. On the live
  // storefront each is set exactly once and never re-created.
  //
  // The `*Target` variables hold the DOM ELEMENT each resource watches
  // (the `.buybox` the IntersectionObserver observes; the
  // `[data-countdown]` node the interval ticks). The
  // `shopify:section:unload` handler uses them to scope teardown to the
  // section actually being removed — see EDIT 5 lifecycle hooks below.
  let stickyObserver = null;
  let stickyObserverTarget = null;
  let countdownIntervalId = null;
  let countdownTarget = null;

  /* ------------- BOOT ------------- */
  // EDIT 5 — boot() is now fully idempotent. v2.1's PDP was a single
  // Shopify section, so the original boot used a one-shot module-level
  // "already booted" window flag to block a double-init. The
  // section-slice splits the PDP into 15 sections served by this one
  // shared asset, so the Shopify theme editor re-renders individual
  // sections via shopify:section:load and boot() MUST be allowed to
  // re-run. The re-entry flag is gone; instead every init function is
  // idempotent (bindOnce-guarded listeners, replaceChildren rebuilds,
  // observer/interval teardown), so re-running boot() never double-binds.
  //
  // LIVE STOREFRONT: the script is loaded once by the pdp layout, the
  // full 15-section DOM exists at DOMContentLoaded, boot() runs exactly
  // once — behaviour is byte-for-byte identical to before EDIT 5.
  function boot() {
    initGallery(document.querySelector('.gallery'));
    initPackSelector();
    initQtyStepper();
    initScrollToTiers();
    initFAQ();
    initStickyATC();
    initCountdown();
    initDispatchEstimate();
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot);
  } else {
    boot();
  }

  // EDIT 5 — theme-editor lifecycle hooks. Gated on Shopify.designMode so
  // they are inert on the live storefront (where Shopify.designMode is
  // undefined) and the storefront code path above is the ONLY thing that
  // runs. Inside the theme editor, editing / adding / removing one of the
  // 15 PDP sections re-renders just that section's DOM with no handlers;
  // shopify:section:load re-runs the idempotent boot() so the re-rendered
  // section regains interactivity without a full page reload.
  //
  // shopify:section:unload tears down ONLY the observer / interval the
  // about-to-be-removed section actually owns. ADR 0017 Phase 7 fold
  // (FOLD ITEM 2 — SHOULD-FIX): the previous handler tore both down
  // GLOBALLY on ANY of the 15 PDP sections unloading, so removing (say)
  // the FAQ section killed the hero's sticky-ATC observer + the
  // announcement countdown and never re-created them (no hero / no
  // announcement `section:load` fires). The teardown is now scoped:
  // `event.target` is the unloading section element, so we disconnect
  // the observer only when the `.buybox` it watches is inside that
  // section, and clear the interval only when its `[data-countdown]`
  // node is inside that section. A non-owning section unload leaves both
  // resources untouched. (`shopify:section:load` stays a full idempotent
  // boot() — bindOnce + replaceChildren rebuilds + observer/interval
  // teardown-before-recreate make a re-run of an unchanged section a
  // no-op, so the broad re-init does not corrupt sibling sections.)
  if (window.Shopify && Shopify.designMode) {
    document.addEventListener('shopify:section:load', boot);
    document.addEventListener('shopify:section:unload', function (event) {
      const removed = event.target;
      // Disconnect the sticky-ATC observer ONLY if the `.buybox` it
      // observes belongs to the section being removed.
      if (
        stickyObserver &&
        stickyObserverTarget &&
        removed &&
        typeof removed.contains === 'function' &&
        removed.contains(stickyObserverTarget)
      ) {
        stickyObserver.disconnect();
        stickyObserver = null;
        stickyObserverTarget = null;
      }
      // Clear the countdown interval ONLY if the `[data-countdown]` node
      // it ticks belongs to the section being removed.
      if (
        countdownIntervalId !== null &&
        countdownTarget &&
        removed &&
        typeof removed.contains === 'function' &&
        removed.contains(countdownTarget)
      ) {
        clearInterval(countdownIntervalId);
        countdownIntervalId = null;
        countdownTarget = null;
      }
    });
  }
})();
